import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WorkPage, WorkSection } from '@web/components/work-page.tsx';
import { usePlanFeature } from '@web/components/plan-gate.tsx';
import { useSession } from '@web/state/session.tsx';
import { useDocumentTitle } from '@web/hooks/index.ts';
import '../reports.css';
import {
  REPORT_GROUPS,
  reportSectionLocked,
  reportSectionPlanName,
  visibleReportSections,
  type ReportGroup,
  type Section,
} from './shared.tsx';
import { useReportPreferences } from './preferences.ts';

/**
 * `/reports` with the hub on: every report this company can open, as cards
 * under the four questions they answer (Money, Customers, Team, Quality).
 * Each card opens one report on its own route. Nothing is fetched here — the
 * hub is a table of contents, not a dashboard.
 */
export default function ReportsHubPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('reports.documentTitle'));
  const hasReports = usePlanFeature('reports_core');
  const { company } = useSession();
  const [preferences] = useReportPreferences();
  const sections = visibleReportSections(company, hasReports);
  const lastOpened =
    preferences.lastSection !== null && sections.includes(preferences.lastSection) ? preferences.lastSection : null;

  return (
    <WorkPage
      title={t('reports.title')}
      description={t('reports.description')}
      actions={
        lastOpened ? (
          <Link to={`/reports/${lastOpened}`} className="btn btn-secondary">
            {t('reports.hub.lastOpened', { report: t(`reports.tab.${lastOpened}`) })}
          </Link>
        ) : null
      }
    >
      {(Object.keys(REPORT_GROUPS) as ReportGroup[]).map((group) => {
        const groupSections = (REPORT_GROUPS[group] as readonly Section[]).filter((value) => sections.includes(value));
        if (groupSections.length === 0) return null;
        return (
          <WorkSection key={group} title={t(`reports.groups.${group}`)}>
            <ul className="report-hub-grid">
              {groupSections.map((value) => (
                <li key={value}>
                  <Link to={`/reports/${value}`} className="report-hub-card">
                    <span className="report-hub-card-title">
                      {t(`reports.tab.${value}`)}
                      {reportSectionLocked(company, value) ? (
                        <span className="ml-1.5 badge">{reportSectionPlanName(value)}</span>
                      ) : null}
                    </span>
                    <span className="report-hub-card-description">{t(`reports.hub.descriptions.${value}`)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </WorkSection>
        );
      })}
    </WorkPage>
  );
}
